/**
 * Questionnaire — main multi-step questionnaire component.
 * Step navigation, progress bar, response collection, view mode switching.
 * Includes branching logic, dynamic step generation, and response collection.
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { type Component } from '@/types';
import type {
  QuestionnaireStep,
  QuestionnaireProperties,
  QuestionDefinition,
  QuestionnaireResponse,
  StepResponse,
} from '@/types/pm-types';
import { useQuestionnaireBranching, computeNextStepId, computeIsLastStep } from '@/hooks/useQuestionnaireBranching';
import { useQuestionnaireResponses } from '@/hooks/useQuestionnaireResponses';
import { aggregateResponses } from '@/utils/questionnaireAggregation';
import { QuestionnaireStepView } from './QuestionnaireStepView';
import { QuestionnaireProgress } from './QuestionnaireProgress';
import { QuestionnaireResults } from './QuestionnaireResults';

// ─── Default sample data ───

const DEFAULT_SAMPLE_STEPS: QuestionnaireStep[] = [
  {
    id: 'sample-step-1',
    title: 'Welcome',
    description: 'Let us get to know you a little.',
    order: 0,
    questions: [
      { id: 'sq1', type: 'text', label: 'What is your name?', required: true, placeholder: 'Enter your name' },
      { id: 'sq2', type: 'boolean', label: 'Are you a returning user?', required: false },
    ],
  },
  {
    id: 'sample-step-2',
    title: 'Preferences',
    description: 'Tell us what you like.',
    order: 1,
    questions: [
      {
        id: 'sq3', type: 'single-select', label: 'Pick your favorite category', required: true,
        options: [
          { value: 'tech', label: 'Technology' },
          { value: 'design', label: 'Design' },
          { value: 'business', label: 'Business' },
        ],
      },
      {
        id: 'sq4', type: 'multi-select', label: 'Which topics interest you?', required: false,
        options: [
          { value: 'ai', label: 'AI / ML' },
          { value: 'web', label: 'Web Dev' },
          { value: 'mobile', label: 'Mobile' },
          { value: 'data', label: 'Data Science' },
        ],
      },
    ],
  },
  {
    id: 'sample-step-3',
    title: 'Feedback',
    description: 'Rate your experience so far.',
    order: 2,
    questions: [
      { id: 'sq5', type: 'rating', label: 'Rate your experience', required: false },
      { id: 'sq6', type: 'text', label: 'Any additional comments?', required: false, placeholder: 'Optional feedback...' },
    ],
  },
];

// ─── Dynamic Options Resolver (Phase F) ───

function resolveDynamicOptions(
  step: QuestionnaireStep,
  allResponses: Record<string, Record<string, unknown>>,
): QuestionDefinition[] {
  return step.questions.flatMap((question) => {
    if (!question.dynamicOptions) return [question];

    const { sourceStepId, sourceQuestionId, optionsPerCategory } = question.dynamicOptions;
    const sourceStepResponses = allResponses[sourceStepId];
    if (!sourceStepResponses) return [question];

    const selectedValues = sourceStepResponses[sourceQuestionId];
    if (!Array.isArray(selectedValues) || selectedValues.length === 0) return [question];

    // Generate a question per selected category
    return selectedValues.map((category: unknown) => ({
      ...question,
      id: `${question.id}_${String(category)}`,
      label: `${String(category)}: ${question.label}`,
      options: optionsPerCategory,
      dynamicOptions: undefined, // resolved — no longer dynamic
      _dynamicSection: String(category), // marker for section headers
    }));
  });
}

// ─── Component Props ───

interface QuestionnaireComponentProps {
  component: Component;
  data?: Record<string, unknown>[];
  isViewerMode?: boolean;
}

// ─── Main Component ───

export const Questionnaire: React.FC<QuestionnaireComponentProps> = ({
  component,
  data,
  isViewerMode = false,
}) => {
  const { properties = {} } = component;
  const props = properties as Partial<QuestionnaireProperties>;

  const steps = useMemo(() => {
    const s = props.steps;
    if (Array.isArray(s) && s.length > 0) return [...s].sort((a, b) => a.order - b.order);
    return DEFAULT_SAMPLE_STEPS;
  }, [props.steps]);

  const accentColor = props.accentColor || '#2563eb';
  const showProgress = props.showProgress !== false;
  const allowSkip = props.allowSkip || false;
  const allowBack = props.allowBackNavigation !== false;
  const submitLabel = props.submitLabel || 'Submit';
  const completionMessage = props.completionMessage || 'Thank you for your responses!';
  const questionLayout = props.questionLayout || 'stacked';
  const viewMode = props.viewMode || 'form';

  // Branching logic (Phase E — extracted hook)
  const branching = useQuestionnaireBranching(steps);

  // Generate a stable anonymous respondent ID if auth is not wired.
  // Uses sessionStorage so each browser tab gets a consistent ID across re-renders
  // but different tabs get different IDs (simulating different users).
  const respondentId = useMemo(() => {
    if (typeof window === 'undefined') return 'anon';
    const key = `questionnaire_respondent_${component.id}`;
    let id = sessionStorage.getItem(key);
    if (!id) {
      id = `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem(key, id);
    }
    return id;
  }, [component.id]);

  // Response collection (Phase G — data-service integration)
  const responseHook = useQuestionnaireResponses({
    questionnaireId: component.id,
    writebackConfig: props.writebackConfig,
    respondentId,
    respondentName: undefined,
    steps,
    allowMultipleSubmissions: props.allowMultipleSubmissions,
  });

  // Step navigation state
  const [currentStepId, setCurrentStepId] = useState<string>(steps[0]?.id || '');
  const [stepPath, setStepPath] = useState<string[]>([steps[0]?.id || '']);
  const [visitedStepIds, setVisitedStepIds] = useState<string[]>([steps[0]?.id || '']);
  const [completedStepIds, setCompletedStepIds] = useState<string[]>([]);

  // Response state (local — used for form interaction; synced to data-service via hook)
  const [allAnswers, setAllAnswers] = useState<Record<string, Record<string, unknown>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Reset when steps change
  useEffect(() => {
    if (steps.length > 0 && !steps.find((s) => s.id === currentStepId)) {
      setCurrentStepId(steps[0].id);
      setStepPath([steps[0].id]);
      setVisitedStepIds([steps[0].id]);
      setCompletedStepIds([]);
      setAllAnswers({});
      setSubmitted(false);
      branching.resetPath(steps[0].id);
    }
  }, [steps, currentStepId, branching]);

  const currentStep = useMemo(() => steps.find((s) => s.id === currentStepId), [steps, currentStepId]);
  const currentStepIndex = useMemo(() => steps.findIndex((s) => s.id === currentStepId), [steps, currentStepId]);
  const currentAnswers = allAnswers[currentStepId] || {};

  // Flatten all answers for branch evaluation
  const flatAnswers = useMemo(() => {
    const flat: Record<string, unknown> = {};
    for (const [, stepAnswers] of Object.entries(allAnswers)) {
      Object.assign(flat, stepAnswers);
    }
    return flat;
  }, [allAnswers]);

  // Dynamic options resolution
  const resolvedQuestions = useMemo(() => {
    if (!currentStep) return undefined;
    const hasAnyDynamic = currentStep.questions.some((q) => q.dynamicOptions);
    if (!hasAnyDynamic) return undefined;
    return resolveDynamicOptions(currentStep, allAnswers);
  }, [currentStep, allAnswers]);

  const handleAnswerChange = useCallback((questionId: string, value: unknown) => {
    setAllAnswers((prev) => ({
      ...prev,
      [currentStepId]: {
        ...(prev[currentStepId] || {}),
        [questionId]: value,
      },
    }));
    // Clear validation errors on change
    setValidationErrors([]);
  }, [currentStepId]);

  const validateCurrentStep = useCallback((): boolean => {
    if (!currentStep) return true;
    const questions = resolvedQuestions || currentStep.questions;
    const errors: string[] = [];

    for (const q of questions) {
      if (q.required) {
        const answer = currentAnswers[q.id];
        if (answer === undefined || answer === null || answer === '' ||
            (Array.isArray(answer) && answer.length === 0)) {
          errors.push(`"${q.label}" is required`);
        }
      }
    }

    setValidationErrors(errors);
    return errors.length === 0;
  }, [currentStep, resolvedQuestions, currentAnswers]);

  const handleNext = useCallback(() => {
    if (!validateCurrentStep() && !allowSkip) return;

    // Mark current step as completed
    setCompletedStepIds((prev) => prev.includes(currentStepId) ? prev : [...prev, currentStepId]);

    // Auto-save step to data service (fire-and-forget)
    if (responseHook.isPersistenceEnabled && currentAnswers && Object.keys(currentAnswers).length > 0) {
      responseHook.saveStepResponse(currentStepId, currentAnswers).catch(() => {});
    }

    const nextId = computeNextStepId(currentStepId, steps, flatAnswers, stepPath);
    if (nextId) {
      setCurrentStepId(nextId);
      setStepPath((prev) => [...prev, nextId]);
      setVisitedStepIds((prev) => prev.includes(nextId) ? prev : [...prev, nextId]);
      branching.pushStepPath(nextId);
    }
  }, [currentStepId, steps, flatAnswers, stepPath, validateCurrentStep, allowSkip, responseHook, currentAnswers, branching]);

  const handlePrevious = useCallback(() => {
    if (stepPath.length > 1) {
      const newPath = stepPath.slice(0, -1);
      setStepPath(newPath);
      setCurrentStepId(newPath[newPath.length - 1]);
      branching.popStepPath();
    }
  }, [stepPath, branching]);

  const handleSubmit = useCallback(() => {
    if (!validateCurrentStep() && !allowSkip) return;

    // Mark last step completed
    setCompletedStepIds((prev) => prev.includes(currentStepId) ? prev : [...prev, currentStepId]);

    // Submit to data service
    if (responseHook.isPersistenceEnabled) {
      responseHook.submitResponse(allAnswers).catch(() => {});
    }

    setSubmitted(true);
  }, [currentStepId, validateCurrentStep, allowSkip, responseHook, allAnswers]);

  const isOnLastStep = currentStep ? computeIsLastStep(currentStepId, steps, flatAnswers, stepPath) : false;

  // Mock responses from data prop (for results view when no persistence)
  const mockResponses: QuestionnaireResponse[] = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    return data.map((record, i) => ({
      questionnaireId: component.id,
      respondentId: String(record['respondentId'] || `user-${i}`),
      respondentName: String(record['respondentName'] || `User ${i + 1}`),
      status: 'completed' as const,
      responses: (record['responses'] || {}) as Record<string, StepResponse>,
      startedAt: String(record['startedAt'] || new Date().toISOString()),
      completedAt: String(record['completedAt'] || new Date().toISOString()),
    }));
  }, [data, component.id]);

  // Use data-service responses if available, otherwise fall back to mock data
  const resultsResponses = responseHook.allResponses.length > 0 ? responseHook.allResponses : mockResponses;
  const resultsAggregated = useMemo(() => {
    if (resultsResponses.length === 0) return null;
    return aggregateResponses(resultsResponses, steps);
  }, [resultsResponses, steps]);

  // ─── Results view ───
  if (viewMode === 'results') {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        borderRadius: 8,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '12px 16px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span style={{ fontSize: 16 }}>📊</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>
            {props.title || 'Questionnaire Results'}
          </span>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {responseHook.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32, color: '#9ca3af' }}>
              Loading responses...
            </div>
          ) : (
            <QuestionnaireResults
              steps={steps}
              responses={resultsResponses}
              aggregated={resultsAggregated}
              accentColor={accentColor}
            />
          )}
        </div>
      </div>
    );
  }

  // ─── Submitted view ─── (check both local state and persisted status)
  const alreadyCompleted = !props.allowMultipleSubmissions &&
    responseHook.myResponse?.status === 'completed';
  if (submitted || alreadyCompleted) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        background: '#fff',
        borderRadius: 8,
        padding: 32,
      }}>
        <div style={{ fontSize: 48 }}>✅</div>
        <p style={{ fontSize: 16, fontWeight: 500, color: '#111827', textAlign: 'center', margin: 0 }}>
          {completionMessage}
        </p>
        {responseHook.isSaving && (
          <span style={{ fontSize: 12, color: '#9ca3af' }}>Saving your response...</span>
        )}
        {responseHook.error && (
          <span style={{ fontSize: 12, color: '#dc2626' }}>{responseHook.error}</span>
        )}
        <button
          type="button"
          onClick={() => {
            setSubmitted(false);
            setCurrentStepId(steps[0]?.id || '');
            setStepPath([steps[0]?.id || '']);
            setVisitedStepIds([steps[0]?.id || '']);
            setCompletedStepIds([]);
            setAllAnswers({});
            setValidationErrors([]);
            branching.resetPath(steps[0]?.id || '');
          }}
          style={{
            padding: '8px 20px',
            border: `1px solid ${accentColor}`,
            borderRadius: 6,
            background: 'transparent',
            color: accentColor,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Start Over
        </button>
      </div>
    );
  }

  // ─── Form view ───
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        borderRadius: 8,
        overflow: 'hidden',
      }}
      data-testid="questionnaire-component"
    >
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>📋</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>
            {props.title || 'Questionnaire'}
          </span>
          {responseHook.isSaving && (
            <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>Saving...</span>
          )}
        </div>
        {showProgress && (
          <QuestionnaireProgress
            steps={steps}
            currentStepIndex={currentStepIndex}
            visitedStepIds={visitedStepIds}
            completedStepIds={completedStepIds}
            accentColor={accentColor}
          />
        )}
      </div>

      {/* Step content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {currentStep && (
          <QuestionnaireStepView
            step={currentStep}
            answers={currentAnswers}
            onAnswerChange={handleAnswerChange}
            questionLayout={questionLayout}
            accentColor={accentColor}
            disabled={isViewerMode}
            resolvedQuestions={resolvedQuestions}
          />
        )}

        {/* Validation errors */}
        {validationErrors.length > 0 && (
          <div style={{
            marginTop: 12,
            padding: '8px 12px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
            fontSize: 13,
            color: '#dc2626',
          }}>
            {validationErrors.map((err, i) => (
              <div key={i}>{err}</div>
            ))}
          </div>
        )}

        {/* Data-service error */}
        {responseHook.error && (
          <div style={{
            marginTop: 8,
            padding: '6px 10px',
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 6,
            fontSize: 12,
            color: '#92400e',
          }}>
            {responseHook.error}
          </div>
        )}
      </div>

      {/* Navigation footer */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
      }}>
        <button
          type="button"
          onClick={handlePrevious}
          disabled={stepPath.length <= 1 || !allowBack}
          style={{
            padding: '8px 16px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            background: '#fff',
            color: stepPath.length <= 1 || !allowBack ? '#d1d5db' : '#374151',
            fontSize: 13,
            cursor: stepPath.length <= 1 || !allowBack ? 'default' : 'pointer',
            opacity: stepPath.length <= 1 || !allowBack ? 0.5 : 1,
          }}
        >
          ← Previous
        </button>

        <div style={{ display: 'flex', gap: 8 }}>
          {allowSkip && !isOnLastStep && (
            <button
              type="button"
              onClick={handleNext}
              style={{
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                borderRadius: 6,
                background: '#fff',
                color: '#6b7280',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Skip
            </button>
          )}

          {isOnLastStep ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isViewerMode || responseHook.isSaving}
              style={{
                padding: '8px 20px',
                border: 'none',
                borderRadius: 6,
                background: accentColor,
                color: '#fff',
                fontSize: 13,
                fontWeight: 500,
                cursor: isViewerMode || responseHook.isSaving ? 'default' : 'pointer',
                opacity: isViewerMode || responseHook.isSaving ? 0.5 : 1,
              }}
            >
              {responseHook.isSaving ? 'Submitting...' : submitLabel}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNext}
              style={{
                padding: '8px 20px',
                border: 'none',
                borderRadius: 6,
                background: accentColor,
                color: '#fff',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

Questionnaire.displayName = 'Questionnaire';
